"use client";

import { useCallback, useEffect, useState } from "react";
import { useRouter } from "next/navigation";
import { Command } from "cmdk";
import { Search } from "lucide-react";
import { Button } from "@/components/ui/button";
import { docsNav } from "@/lib/docs/docsNav";

interface SearchItem {
  title: string;
  section: string;
  href: string;
}

function buildSearchItems(): SearchItem[] {
  const items: SearchItem[] = [];
  for (const section of docsNav) {
    items.push({
      title: section.title,
      section: section.title,
      href: `/docs/${section.basePath}`,
    });
    for (const item of section.items) {
      items.push({
        title: item.title,
        section: section.title,
        href: `/docs/${section.basePath}/${item.slug}`,
      });
    }
  }
  // FAQ
  items.push({ title: "FAQ", section: "Help", href: "/docs/faq" });
  return items;
}

export function DocsSearch() {
  const [open, setOpen] = useState(false);
  const router = useRouter();
  const items = buildSearchItems();

  const handleKeyDown = useCallback((e: KeyboardEvent) => {
    if ((e.metaKey || e.ctrlKey) && e.key === "k") {
      e.preventDefault();
      setOpen((prev) => !prev);
    }
  }, []);

  useEffect(() => {
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [handleKeyDown]);

  return (
    <>
      <Button
        variant="outline"
        size="sm"
        aria-label="Search docs"
        className="gap-2 text-muted-foreground font-normal w-9 px-0 justify-center sm:w-48 sm:px-3 sm:justify-start"
        onClick={() => setOpen(true)}
      >
        <Search className="h-3.5 w-3.5" />
        <span className="hidden sm:inline flex-1 text-left">Search docs...</span>
        <kbd className="hidden sm:inline-flex h-5 items-center gap-0.5 rounded border bg-muted px-1.5 font-mono text-3xs font-medium text-muted-foreground">
          <span className="text-xs">&#8984;</span>K
        </kbd>
      </Button>

      {open && (
        <div className="fixed inset-0 z-50">
          <div
            className="fixed inset-0 bg-black/50"
            onClick={() => setOpen(false)}
          />
          <div className="fixed top-[20%] left-1/2 -translate-x-1/2 w-full max-w-lg">
            <Command
              className="rounded-lg border bg-popover text-popover-foreground shadow-lg"
              shouldFilter={true}
            >
              <Command.Input
                placeholder="Search documentation..."
                className="flex h-11 w-full rounded-t-lg bg-transparent px-4 py-3 text-sm outline-none placeholder:text-muted-foreground"
                autoFocus
              />
              <Command.List className="max-h-72 overflow-y-auto p-2">
                <Command.Empty className="py-6 text-center text-sm text-muted-foreground">
                  No results found.
                </Command.Empty>
                {docsNav.map((section) => (
                  <Command.Group
                    key={section.basePath}
                    heading={section.title}
                    className="[&_[cmdk-group-heading]]:px-2 [&_[cmdk-group-heading]]:py-1.5 [&_[cmdk-group-heading]]:text-xs [&_[cmdk-group-heading]]:font-medium [&_[cmdk-group-heading]]:text-muted-foreground"
                  >
                    <Command.Item
                      value={`${section.title} Overview`}
                      onSelect={() => {
                        router.push(`/docs/${section.basePath}`);
                        setOpen(false);
                      }}
                      className="relative flex cursor-pointer select-none items-center rounded-sm px-2 py-1.5 text-sm outline-none aria-selected:bg-accent aria-selected:text-accent-foreground"
                    >
                      Overview
                    </Command.Item>
                    {section.items.map((item) => (
                      <Command.Item
                        key={item.slug}
                        value={`${section.title} ${item.title}`}
                        onSelect={() => {
                          router.push(
                            `/docs/${section.basePath}/${item.slug}`
                          );
                          setOpen(false);
                        }}
                        className="relative flex cursor-pointer select-none items-center rounded-sm px-2 py-1.5 text-sm outline-none aria-selected:bg-accent aria-selected:text-accent-foreground"
                      >
                        {item.title}
                      </Command.Item>
                    ))}
                  </Command.Group>
                ))}
              </Command.List>
            </Command>
          </div>
        </div>
      )}
    </>
  );
}
